import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { ShoppingBag, Info, MessageCircle, Sparkles, Tag, Loader2, Heart, Wallet } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { track as analytics } from "../lib/analytics";
import useExperiment, { variantsSnapshot } from "../hooks/useExperiment";
import { EXPERIMENTS } from "../lib/experiments";
import { toast } from "sonner";

export default function ProductCard({ rec, whatsappNumber }) {
  const { product, why, how, benefits, usage, is_best_match, country_variant } = rec || {};
  const [subscribe, setSubscribe] = useState(true);
  const subBanner = useExperiment(EXPERIMENTS.SUBSCRIBE_BANNER);
  const waStyle = useExperiment(EXPERIMENTS.WHATSAPP_STYLE);
  const [coupon, setCoupon] = useState("");
  const [validating, setValidating] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [checkingOut, setCheckingOut] = useState(false);
  const [waLoading, setWaLoading] = useState(false);

  const baseGbp = Number(product.price_gbp || 0);
  // Admin-controlled pricing: promo_mode decides whether subscribers
  // see a percentage discount, a free gift, or neither.
  const promoMode = product.promo_mode || "percent";
  const adminDiscount = Math.max(0, Math.min(90, Number(product.discount_percent ?? 10)));
  const subDiscount = (subscribe && promoMode === "percent") ? adminDiscount : 0;
  const couponDiscount = appliedCoupon
    ? appliedCoupon.type === "percent"
      ? Number(appliedCoupon.value)
      : Math.min(100, (Number(appliedCoupon.value) / Math.max(baseGbp, 0.01)) * 100)
    : 0;
  const totalDiscount = Math.min(80, subDiscount + couponDiscount);
  // Explicit subscription_price_gbp override wins over computed discount.
  const subOverride = Number(product.subscription_price_gbp || 0);
  const computedFinal = baseGbp ? Math.max(0.5, +(baseGbp * (1 - totalDiscount / 100)).toFixed(2)) : 0;
  const finalGbp = (subscribe && promoMode === "percent" && subOverride > 0)
    ? subOverride
    : computedFinal;
  const showGift = subscribe && promoMode === "gift" && product.gift_enabled && product.gift_title;

  const track = async (event_type) => {
    try {
      await api.post("/engagement", { event_type, product_id: product.id });
    } catch {
      /* ignore */
    }
  };

  const validateCoupon = async () => {
    if (!coupon.trim()) return;
    setValidating(true);
    try {
      const { data } = await api.get(`/coupons/validate`, { params: { code: coupon.trim() } });
      setAppliedCoupon(data);
      toast.success(`Coupon applied: -${data.value}${data.type === "percent" ? "%" : "£"}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invalid coupon");
      setAppliedCoupon(null);
    } finally {
      setValidating(false);
    }
  };

  const checkout = async (mode) => {
    setCheckingOut(true);
    try {
      track(mode === "subscribe" ? "buy_click_subscribe" : "buy_click");
      // GA4 e-commerce: add_to_cart fires immediately before checkout_started,
      // matching Google's recommended Enhanced Ecommerce funnel order.
      ga("add_to_cart", {
        currency: "GBP",
        value: finalGbp,
        items: [{
          item_id: product.id,
          item_name: product.name,
          price: finalGbp,
          quantity: 1,
          item_variant: mode,
        }],
      });
      analytics.checkoutStart({
        product_id: product.id,
        product_name: product.name,
        price_gbp: finalGbp,
        mode,
        coupon_code: appliedCoupon?.code || (coupon.trim() ? coupon.trim() : null),
        discount_pct: totalDiscount,
        variants: variantsSnapshot([
          EXPERIMENTS.SUBSCRIBE_BANNER,
          EXPERIMENTS.LANDING_HEADLINE,
          EXPERIMENTS.LANDING_CTA,
          EXPERIMENTS.RESULT_URGENCY_TONE,
        ]),
      });
      const payload = {
        product_id: product.id,
        origin_url: window.location.origin,
        mode,
        coupon_code: appliedCoupon?.code || (coupon.trim() ? coupon.trim() : undefined),
      };
      const { data } = await api.post("/checkout/session", payload);
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error("Couldn't start checkout. Please try again.");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setCheckingOut(false);
    }
  };

  const openWhatsApp = async () => {
    setWaLoading(true);
    track("whatsapp_click");
    analytics.whatsappClick("product_card", product.id);
    try {
      const params = product?.id ? { product_id: product.id } : {};
      params.style = waStyle.content.style;
      const { data } = await api.get(`/whatsapp/message`, { params });
      const number = data.whatsapp_number || whatsappNumber || "";
      const text = encodeURIComponent(data.message || "Hi, I would like guidance based on my results.");
      const url = number ? `https://wa.me/${number}?text=${text}` : `https://wa.me/?text=${text}`;
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      const fallback = encodeURIComponent(
        `Hi, I'm interested in ${product.name}. I would like guidance based on my results.`
      );
      const url = whatsappNumber ? `https://wa.me/${whatsappNumber}?text=${fallback}` : `https://wa.me/?text=${fallback}`;
      window.open(url, "_blank", "noopener,noreferrer");
    } finally {
      setWaLoading(false);
    }
  };

  return (
    <Card className={`bg-white border rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-shadow ${
      is_best_match ? "border-amber-300 ring-2 ring-amber-200/60" : "border-emerald-100"
    }`} data-testid={`product-card-${product.id}`}>
      {is_best_match ? (
        <div className="bg-gradient-to-r from-amber-400 to-amber-500 text-emerald-950 text-[11px] uppercase tracking-[0.2em] font-bold px-4 py-1.5 flex items-center gap-2" data-testid={`best-match-banner-${product.id}`}>
          <Sparkles className="w-3.5 h-3.5" /> Best match for you · top recommendation
        </div>
      ) : null}
      <div className="flex flex-col md:flex-row">
        <div className="md:w-52 md:shrink-0 bg-emerald-50/60 p-5 flex items-center justify-center">
          {product.image_url ? (
            <img
              src={product.image_url}
              alt={product.name}
              className="w-full h-32 md:h-40 object-cover rounded-xl"
            />
          ) : (
            <div className="w-full h-32 md:h-40 rounded-xl bg-emerald-100 flex items-center justify-center">
              <Sparkles className="w-8 h-8 text-emerald-700" />
            </div>
          )}
        </div>
        <CardContent className="p-6 flex-1">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <Badge className="bg-amber-400/90 text-emerald-950 hover:bg-amber-400 border-none">
              Recommended for you
            </Badge>
            {baseGbp ? (
              <span className="text-sm text-emerald-700 font-semibold">£{baseGbp.toFixed(0)}</span>
            ) : null}
          </div>
          <h3 className="font-display text-xl md:text-2xl text-emerald-950 mb-1">{product.name}</h3>
          <p className="text-sm text-emerald-900/70 mb-4">{product.description}</p>

          {/* Country-aware quick link to the full concierge page */}
          {product.slug && country_variant && (
            <Link
              to={`/shop/${product.slug}`}
              className="inline-flex items-center text-[12px] text-emerald-700 hover:text-emerald-800 mb-3 underline-offset-2 hover:underline"
              data-testid={`product-shop-link-${product.id}`}
            >
              <Info className="w-3 h-3 mr-1" /> View full product page, PDFs &amp; ingredients ({country_variant.country})
            </Link>
          )}

          {/* Trust-building "why this product?" panel — first thing users see */}
          <div
            className="mb-5 rounded-xl border border-emerald-100 bg-gradient-to-br from-emerald-50/60 via-white to-amber-50/30 p-4 sm:p-5"
            data-testid={`why-panel-${product.id}`}
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-700 text-white">
                <Heart className="w-3.5 h-3.5" />
              </span>
              <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold">
                Why we matched this to you
              </div>
            </div>
            <p className="text-sm sm:text-base text-emerald-950 leading-relaxed">{why}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 mb-5">
            <Field title="How it works" body={how} />
            <Field title="Expected outcomes" body={benefits} />
            <Field title="Suggested usage" body={usage} />
          </div>

          {/* Subscribe + coupon section */}
          {baseGbp > 0 ? (
            <div className={`mb-5 rounded-xl border-2 p-4 transition-colors ${
              subscribe && subBanner.content.accent === "strong"
                ? "border-emerald-700 bg-emerald-50/70"
                : subscribe && subBanner.content.accent === "soft"
                ? "border-emerald-300 bg-emerald-50/40"
                : "border-emerald-100 bg-emerald-50/30"
            }`} data-testid={`subscribe-banner-${product.id}`} data-variant={subBanner.variant}>
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <Switch
                    id={`sub-${product.id}`}
                    checked={subscribe}
                    onCheckedChange={setSubscribe}
                    data-testid={`subscribe-toggle-${product.id}`}
                  />
                  <Label htmlFor={`sub-${product.id}`} className="text-sm text-emerald-950 cursor-pointer">
                    <span className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold">
                        {promoMode === "gift" && product.gift_title
                          ? `Subscribe & get a ${product.gift_title.toLowerCase()}`
                          : promoMode === "none"
                          ? "Subscribe monthly"
                          : `Subscribe & save ${adminDiscount}%`}
                      </span>
                      {subBanner.content.showBadge ? (
                        <span
                          className={`text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-0.5 rounded-full ${
                            subBanner.content.accent === "strong"
                              ? "bg-amber-400 text-emerald-950"
                              : "bg-emerald-100 text-emerald-800"
                          }`}
                        >
                          {subBanner.content.badgeText}
                        </span>
                      ) : null}
                    </span>
                    <span className="block text-xs text-emerald-900/70 mt-1">
                      {subBanner.content.copy}
                    </span>
                  </Label>
                </div>
                <div className="text-right">
                  {totalDiscount > 0 ? (
                    <>
                      <div className="text-xs text-emerald-900/60 line-through">£{baseGbp.toFixed(2)}</div>
                      <div className="font-display text-2xl text-emerald-800" data-testid={`final-price-${product.id}`}>
                        £{finalGbp.toFixed(2)}
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.2em] text-amber-700 font-semibold">
                        Save {totalDiscount.toFixed(0)}%
                      </div>
                    </>
                  ) : (
                    <div className="font-display text-2xl text-emerald-950" data-testid={`final-price-${product.id}`}>
                      £{baseGbp.toFixed(2)}
                    </div>
                  )}
                </div>
              </div>
              {showGift ? (
                <div className="mt-3 flex items-center gap-3 rounded-xl bg-white border border-amber-200 p-3" data-testid={`gift-banner-${product.id}`}>
                  {product.gift_image_url ? (
                    <img
                      src={product.gift_image_url}
                      alt={product.gift_title}
                      className="w-12 h-12 object-cover rounded-lg flex-shrink-0"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center text-amber-700 flex-shrink-0">
                      <Sparkles className="w-5 h-5" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-amber-700 font-bold">
                      Free with subscription
                    </div>
                    <div className="text-sm font-semibold text-emerald-950 truncate">
                      {product.gift_title}
                    </div>
                  </div>
                </div>
              ) : null}
              <div className="mt-3 flex items-center gap-2">
                <Tag className="w-4 h-4 text-emerald-700" />
                <Input
                  value={coupon}
                  onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                  placeholder="Got a referral or promo code?"
                  className="rounded-xl border-emerald-200 h-9 text-sm"
                  data-testid={`coupon-input-${product.id}`}
                />
                <Button
                  type="button"
                  size="sm"
                  variant={appliedCoupon ? "outline" : "default"}
                  className={
                    appliedCoupon
                      ? "rounded-full border-emerald-200 text-emerald-800"
                      : "rounded-full bg-emerald-700 hover:bg-emerald-800 text-white"
                  }
                  onClick={validateCoupon}
                  disabled={validating || !coupon.trim()}
                  data-testid={`coupon-apply-${product.id}`}
                >
                  {validating ? <Loader2 className="w-3 h-3 animate-spin" /> : appliedCoupon ? "Applied" : "Apply"}
                </Button>
              </div>
            </div>
          ) : null}

          <div className="flex flex-wrap gap-3">
            <Button
              type="button"
              onClick={() => checkout(subscribe ? "subscribe" : "one_time")}
              disabled={checkingOut || baseGbp <= 0}
              className="cta-shine rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-6"
              data-testid={`buy-now-${product.id}`}
            >
              <ShoppingBag className="w-4 h-4 mr-2" />
              {checkingOut ? "Redirecting…" : subscribe
                ? (promoMode === "gift" && product.gift_title
                    ? `Subscribe & get your gift`
                    : promoMode === "none"
                    ? "Subscribe"
                    : "Subscribe & save")
                : "Buy Now"}
            </Button>
            <a
              href={product.purchase_link || "#"}
              target="_blank"
              rel="noreferrer noopener"
              onClick={() => track("learn_more")}
              data-testid={`learn-more-${product.id}`}
            >
              <Button variant="outline" className="rounded-full border-emerald-200 text-emerald-900 hover:bg-emerald-50">
                <Info className="w-4 h-4 mr-2" /> Learn more
              </Button>
            </a>
            <Button
              type="button"
              onClick={openWhatsApp}
              disabled={waLoading}
              className="rounded-full bg-amber-400 hover:bg-amber-500 text-emerald-950 px-6"
              data-testid={`whatsapp-${product.id}`}
            >
              <MessageCircle className="w-4 h-4 mr-2" /> Talk to Expert
            </Button>
          </div>
          {baseGbp > 0 ? (
            <div className="mt-3 space-y-1.5">
              <div
                className="flex items-center gap-1.5 text-[11px] text-emerald-900/75 leading-relaxed flex-wrap"
                data-testid={`payment-options-${product.id}`}
              >
                <Wallet className="w-3 h-3 text-emerald-700" />
                <span className="font-medium">Flexible payment options</span>
                <span className="text-emerald-900/55">·</span>
                <span className="text-emerald-900/65">
                  Apple Pay, Google Pay, Klarna &amp; Clearpay supported
                </span>
              </div>
              <p className="text-[11px] text-emerald-900/60 leading-relaxed" data-testid={`legal-microcopy-${product.id}`}>
                Secure checkout powered by Stripe. By continuing you agree to our{" "}
                <Link to="/terms" className="underline hover:text-emerald-700">Terms</Link> ·{" "}
                <Link to="/privacy" className="underline hover:text-emerald-700">Privacy Policy</Link> ·{" "}
                <Link to="/refund" className="underline hover:text-emerald-700">Refund Policy</Link>.
                Wellness recommendations only — not medical advice.
              </p>
            </div>
          ) : null}
        </CardContent>
      </div>
    </Card>
  );
}

function Field({ title, body }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 font-semibold mb-1">
        {title}
      </div>
      <p className="text-sm text-emerald-900/80">{body}</p>
    </div>
  );
}
